import Store from '@ember-data/store';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type {
  BulkActionCustomDeleteCampaign,
  BulkActionResponseCampaignId,
  BulkActionType,
} from 'donor-elf-web/models/bulk-action';
import CampaignModel from 'donor-elf-web/models/campaign';
import AddSomethingModal, {
  type OpenArgs,
} from 'donor-elf-web/services/add-something-modal';
import CampaignService from 'donor-elf-web/services/campaign-service';
import Changeset from 'ember-changeset';
import lookupValidator, {
  type ValidationResult,
} from 'ember-changeset-validations';

interface ComponentArgs {
  openArgs?: OpenArgs;
}

export default class AddSomethingModalDeleteCampaignIndexComponent extends Component<ComponentArgs> {
  constructor(owner: any, args: ComponentArgs) {
    super(owner, args);

    if (!args.openArgs?.deleteCampaignOptions) {
      throw new Error('args.openArgs.deleteCampaignOptions is required');
    }

    this.createChangeset();
  }

  @service declare addSomethingModal: AddSomethingModal;
  @service declare store: Store;
  @service declare campaignService: CampaignService;

  bulkActionType: BulkActionType = 'delete_campaign';

  @tracked bulkActionCustom?: BulkActionCustomDeleteCampaign;
  @tracked changeset: any;
  @tracked hasGiftsAndPledges = false;
  @tracked showValidationErrors = false;
  @tracked deleting = false;
  @tracked deleteError?: any;
  @tracked loading = true;

  get campaign() {
    return this.args.openArgs!.deleteCampaignOptions!.campaign;
  }

  get canDelete() {
    return !this.isRestrictedTopLevelCampaign;
  }

  get changesetValidation(): { [s: string]: () => ValidationResult } {
    return {
      campaignName: this.validateCampaignName,
      moveToCampaignId: this.validateMoveToCampaignId,
    };
  }

  get hasChildCampaigns() {
    return this.campaign.childrenCampaigns.length > 0;
  }

  get isRestrictedTopLevelCampaign() {
    return this.campaign.restricted && this.campaign.parentCampaignId == null;
  }

  get moveToCampaignSelectOptions() {
    if (this.campaign.restricted && this.campaign.parentCampaignId) {
      // if the campaign is restricted, we must move gifts and pledges to
      // the parent campaign
      return [
        {
          val: this.campaign.parentCampaignId,
          label: this.campaign.parentCampaign?.label,
        },
      ];
    }

    return [
      { val: undefined, label: 'None' },
      ...this.campaignService.sortedCampaigns
        .filter(
          (c) =>
            c.restricted === this.campaign.restricted &&
            c.id !== this.campaign.id &&
            !c.isDescendantOf(this.campaign),
        )
        .map((c) => {
          return { val: c.id, label: c.label };
        }),
    ];
  }

  close = () => {
    this.addSomethingModal.close();
  };

  delete = async () => {
    if (this.deleting) {
      return;
    }

    this.deleteError = false;
    this.showValidationErrors = false;

    await this.changeset.validate();

    if (this.changeset.get('isValid')) {
      this.bulkActionCustom = {
        campaign_id: this.campaign.id,
        move_to_campaign_id: this.changeset.get('moveToCampaignId') || '',
      } as BulkActionCustomDeleteCampaign;

      this.deleting = true;
    } else {
      this.showValidationErrors = true;
    }
  };

  handleDidInsert = async () => {
    if (this.canDelete) {
      this.hasGiftsAndPledges = await this.checkForGiftsAndPledges(
        this.campaign,
      );
    }

    this.loading = false;
  };

  onCompleted = async (_response: BulkActionResponseCampaignId) => {
    const { campaign } = this;
    const moveToCampaignId = this.changeset.get('moveToCampaignId');

    await this.campaignService.onCampaignDeleted({
      campaign,
      moveToCampaignId,
    });

    this.addSomethingModal.close();
    this.deleting = false;
  };

  onError = () => {
    this.deleteError = true;
    this.deleting = false;
  };

  validateCampaignName = (): ValidationResult => {
    if (!this.hasChildCampaigns) {
      return true;
    }

    const campaignName = this.changeset.get('campaignName');

    if (campaignName !== this.campaign.label) {
      return 'You must type the full campaign name to confirm deletion';
    }

    return true;
  };

  validateMoveToCampaignId = (): ValidationResult => {
    if (!this.hasGiftsAndPledges) {
      return true;
    }

    const moveToCampaignId = this.changeset.get('moveToCampaignId');

    if (this.campaign.restricted) {
      if (moveToCampaignId !== this.campaign.parentCampaignId) {
        return 'Gifts and pledges from a restricted campaign can only be moved to its parent campaign';
      }
    } else if (
      this.campaignService.campaignsById[moveToCampaignId]?.restricted
    ) {
      return 'Gifts and pledges from a non-restricted campaign cannot be moved to a restricted campaign';
    }

    return true;
  };

  async checkForGiftsAndPledges(campaign: CampaignModel) {
    const campaignIds = campaign.idAndDescendantIds;
    const campaignPledges = (
      await this.store.query('campaign-pledge', {
        filter: { campaign_ids: campaignIds, limit: 1 },
      })
    ).slice();

    const campaignLineItems = (
      await this.store.query('campaign-line-item', {
        filter: { campaign_ids: campaignIds, limit: 1 },
      })
    ).slice();

    return campaignLineItems.length > 0 || campaignPledges.length > 0;
  }

  createChangeset() {
    let { campaign, changesetValidation } = this;

    this.changeset = new Changeset(
      campaign,
      lookupValidator(changesetValidation),
      changesetValidation,
    );

    if (this.campaign.restricted && this.campaign.parentCampaignId) {
      // if the campaign is restricted, we must move gifts and pledges to
      // the parent campaign
      this.changeset.set('moveToCampaignId', this.campaign.parentCampaignId);
    }
  }
}
